import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LayoutWide from "../layouts/LayoutWide.jsx";
import Spinner from "../components/Spinner.jsx";
import IngredientEditor, { IngredientTable, parseIngredients, serializeIngredients } from "../components/IngredientEditor.jsx";
import ProcedureEditor, { ProcedureSteps, parseSteps, serializeSteps } from "../components/ProcedureEditor.jsx";
import UnitConverter from "../components/UnitConverter.jsx";
import { useRecipeQuery, useUpdateRecipe, useDeleteRecipe } from "../hooks/useRecipesQuery.js";

const RECIPE_TYPES = [
  { value: "liquid_culture", label: "Liquid Culture" },
  { value: "bulk_spawn",     label: "Bulk Spawn" },
  { value: "substrate",      label: "Substrate" },
  { value: "other",          label: "Other" },
];

const TYPE_COLORS = {
  liquid_culture: "bg-blue-100 text-blue-700",
  bulk_spawn:     "bg-amber-100 text-amber-700",
  substrate:      "bg-emerald-100 text-emerald-700",
  other:          "bg-gray-100 text-gray-600",
};

function typeLabel(t) {
  return RECIPE_TYPES.find(r => r.value === t)?.label ?? t;
}

export default function RecipeDetail() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const { data: recipe, isLoading, isError } = useRecipeQuery(recipeId);
  const updateRecipe = useUpdateRecipe(recipeId);
  const deleteRecipe = useDeleteRecipe();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [ingredientRows, setIngredientRows] = useState(null);
  const [procedureSteps, setProcedureSteps] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) return <LayoutWide title="Recipe"><Spinner /></LayoutWide>;
  if (isError) return <LayoutWide title="Recipe"><div className="p-4 text-red-600">Recipe not found.</div></LayoutWide>;

  function startEdit() {
    setForm({
      name:        recipe.name,
      recipe_type: recipe.recipe_type,
      notes:       recipe.notes ?? "",
    });
    setIngredientRows(parseIngredients(recipe.ingredients));
    setProcedureSteps(parseSteps(recipe.procedure));
    setSaveError(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setForm(null);
    setIngredientRows(null);
    setProcedureSteps(null);
    setSaveError(null);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setSaveError("Name is required.");
      return;
    }
    setSaveError(null);
    try {
      await updateRecipe.mutateAsync({
        name:        form.name.trim(),
        recipe_type: form.recipe_type,
        ingredients: serializeIngredients(ingredientRows),
        procedure:   serializeSteps(procedureSteps),
        notes:       form.notes.trim() || null,
      });
      setEditing(false);
      setForm(null);
      setIngredientRows(null);
      setProcedureSteps(null);
    } catch {
      setSaveError("Failed to save. Please try again.");
    }
  }

  async function handleDelete() {
    await deleteRecipe.mutateAsync(recipe.id);
    navigate("/recipes");
  }

  const displayType = editing ? form.recipe_type : recipe.recipe_type;

  return (
    <LayoutWide title={recipe.name}>
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={() => navigate("/recipes")} className="text-sm text-gray-400 hover:text-gray-600">
            ← Recipes
          </button>
          {editing ? (
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="text-2xl font-semibold text-cyan-800 border-b-2 border-cyan-400 bg-transparent focus:outline-none"
            />
          ) : (
            <h1 className="text-2xl font-semibold text-cyan-800">{recipe.name}</h1>
          )}
          {editing ? (
            <select
              value={form.recipe_type}
              onChange={e => setForm(f => ({ ...f, recipe_type: e.target.value }))}
              className="text-xs border border-gray-300 rounded-full px-3 py-1 focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              {RECIPE_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          ) : (
            <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${TYPE_COLORS[displayType]}`}>
              {typeLabel(displayType)}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {editing ? (
            <>
              {saveError && <span className="text-xs text-red-600">{saveError}</span>}
              <button
                onClick={handleSave}
                disabled={updateRecipe.isPending}
                className="text-sm bg-cyan-600 text-white rounded px-3 py-1.5 hover:bg-cyan-700 disabled:opacity-50 transition-colors font-medium"
              >
                {updateRecipe.isPending ? "Saving…" : "Save"}
              </button>
              <button onClick={cancelEdit} className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1.5">
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={startEdit}
                className="text-sm border border-gray-300 text-gray-600 rounded px-3 py-1.5 hover:bg-gray-50 transition-colors"
              >
                Edit
              </button>
              {confirmDelete ? (
                <span className="text-sm text-red-600 flex items-center gap-1">
                  Delete?
                  <button onClick={handleDelete} className="font-semibold hover:underline">Yes</button>
                  <span>/</span>
                  <button onClick={() => setConfirmDelete(false)} className="hover:underline">No</button>
                </span>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="text-sm text-gray-400 hover:text-red-500 px-2 py-1.5"
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-5 max-w-2xl">
        <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col gap-6">

          {/* Ingredients */}
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Ingredients</div>
            {editing
              ? <IngredientEditor rows={ingredientRows} onChange={setIngredientRows} />
              : <IngredientTable raw={recipe.ingredients} />
            }
          </div>

          {/* Procedure */}
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Procedure</div>
            {editing
              ? <ProcedureEditor steps={procedureSteps} onChange={setProcedureSteps} />
              : <ProcedureSteps raw={recipe.procedure} />
            }
          </div>

          {/* Notes */}
          <div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Notes</div>
            {editing ? (
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2}
                placeholder="Any additional notes or tips..."
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-y"
              />
            ) : (
              recipe.notes
                ? <p className="text-sm text-gray-700 whitespace-pre-wrap">{recipe.notes}</p>
                : <p className="text-sm text-gray-400 italic">No notes.</p>
            )}
          </div>

          <div className="text-xs text-gray-400 pt-2 border-t border-gray-100">
            Created {new Date(recipe.created_at).toLocaleDateString()}
            {recipe.updated_at !== recipe.created_at && (
              <> · Updated {new Date(recipe.updated_at).toLocaleDateString()}</>
            )}
          </div>
        </div>

        {/* Unit Converter */}
        <UnitConverter />
      </div>
    </LayoutWide>
  );
}
